import type { FAQ } from './types';

/**
 * English mirror of reseller.es.tsx. Same `id` slugs in both files so
 * deep links resolve in either language.
 */
export const RESELLER_FAQS_EN: FAQ[] = [
  // =============================================================================
  // Customer management
  // =============================================================================
  {
    id: 'customers-create',
    category: 'customers',
    question: 'How do I create a new customer?',
    tags: ['client', 'sub-tenant', 'create'],
    cta: { label: 'Go to Customers', to: '/admin/customers' },
    answer: (
      <p>
        Admin → <strong>Customers</strong> → "Create". Set a name, owner
        email, initial password, country, and timezone. Optionally credit
        them an initial balance. They get an email to sign in and your
        default rate card is pre-assigned.
      </p>
    ),
  },
  {
    id: 'customers-type-picker',
    category: 'customers',
    question: 'Customer vs Reseller — which type do I pick at creation?',
    tags: ['type', 'reseller', 'sub-reseller'],
    answer: (
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>Customer</strong>: end client. Can't have customers of their own.</li>
        <li><strong>Reseller</strong>: can have its own customers underneath. Useful for sub-resellers (white-label of white-label).</li>
        <li>Got it wrong? The detail page has a "Promote to reseller" button.</li>
      </ul>
    ),
  },
  {
    id: 'customers-promote',
    category: 'customers',
    question: 'How do I promote a customer to reseller?',
    tags: ['promote', 'upgrade'],
    answer: (
      <p>
        On the customer detail (<code className="mono">/admin/customers/:id</code>)
        there's a "Promote to reseller" button. Only platform admin sees it.
        It flips <code className="mono">type='reseller'</code> and unlocks
        that tenant's ability to create its own customers.
      </p>
    ),
  },
  {
    id: 'customers-suspend',
    category: 'customers',
    question: 'What happens when I suspend a customer?',
    tags: ['suspend', 'block', 'pause'],
    answer: (
      <p>
        Status flips to <code className="mono">suspended</code>. New sends
        are rejected (<code className="mono">tenant_suspended</code>), but
        the data stays intact. Their users can't sign in. Reactivate with
        a click — everything resumes without loss.
      </p>
    ),
  },
  {
    id: 'customers-impersonate',
    category: 'customers',
    question: 'What does "View as customer" (impersonation) do?',
    tags: ['impersonate', 'view as', 'support'],
    answer: (
      <p>
        Opens a new session acting as the customer's owner. Useful for
        support — you see exactly what the client sees. A topbar banner
        makes the impersonation visible + which admin launched it. The
        audit log records every action you take in that mode.
      </p>
    ),
  },
  {
    id: 'customers-credit-limit',
    category: 'customers',
    question: 'How do I give a customer post-paid credit?',
    tags: ['credit', 'postpaid', 'limit'],
    answer: (
      <p>
        Detail page → "Credit limit". Default is 0 (strict prepaid: balance
        must be &gt; 0 to send). Any positive value flips the customer to
        post-paid up to that amount. The dispatcher accepts sends while
        (balance + credit_limit) ≥ cost.
      </p>
    ),
  },
  {
    id: 'customers-low-balance-threshold',
    category: 'customers',
    question: 'Can I change the low-balance alert threshold?',
    tags: ['threshold', 'alert'],
    answer: (
      <p>
        Yes. Customer detail → "Low balance threshold". Default $10. Below
        that number, the hourly cron sends them an email + paints an in-app
        banner. Set to 0 to disable alerts entirely.
      </p>
    ),
  },

  // =============================================================================
  // Pricing
  // =============================================================================
  {
    id: 'pricing-rate-cards-vs-plans',
    category: 'billing',
    question: 'Rate card vs Plan — what is the difference?',
    tags: ['price', 'tariff'],
    answer: (
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>Rate card</strong>: container of per-destination prices (prefix). One card holds N rates.</li>
        <li><strong>Plan</strong>: subscription package (monthly fee + included SMS + overage) that points at a rate card.</li>
        <li>Typical client: the plan tells them what they pay monthly; the rate card tells them what each extra SMS costs.</li>
      </ul>
    ),
  },
  {
    id: 'pricing-create-rate-card',
    category: 'billing',
    question: 'How do I create a rate card?',
    tags: ['rate card', 'tariff', 'create'],
    cta: { label: 'Go to Rate cards', to: '/admin/rate-cards' },
    answer: (
      <p>
        Admin → <strong>Rate cards</strong> → "Create". Name + strategy
        (LCR / LCD / priority). Then drill into the card and add rates by
        prefix (1 = US, 57 = Colombia, etc) with buy_cost + sell_price +
        which carrier to route through.
      </p>
    ),
  },
  {
    id: 'pricing-strategy',
    category: 'billing',
    question: 'LCR vs LCD vs priority — which strategy do I use?',
    tags: ['strategy', 'routing', 'lcr'],
    answer: (
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>LCR</strong> (least cost routing): picks the cheapest matching rate. Optimizes your margin.</li>
        <li><strong>LCD</strong> (least cost delivery): picks the lowest sell_price. Best price for the client.</li>
        <li><strong>Priority</strong>: the rate's priority wins, ignoring cost. For SLAs / pinning a specific carrier.</li>
      </ul>
    ),
  },
  {
    id: 'pricing-cascade',
    category: 'billing',
    question: 'How does the system pick which rate to apply to a send?',
    tags: ['cascade', 'routing'],
    answer: (
      <p>
        A customer can have multiple rate cards assigned (each with a
        priority). The dispatcher tries them in cascade order: highest-
        priority card first, looking for a rate that matches destination
        + healthy carrier + valid window. First match wins. If nothing
        matches, the send ends in <code className="mono">no_route_available</code>.
      </p>
    ),
  },
  {
    id: 'pricing-assign-card',
    category: 'billing',
    question: 'How do I assign a rate card to a customer?',
    tags: ['assign'],
    answer: (
      <p>
        The customer detail has a "Rate cards" section listing assigned
        cards. "Assign" opens a picker of your cards. Priority defines which
        card is tried first in the cascade. You can have N cards assigned
        per customer.
      </p>
    ),
  },
  {
    id: 'pricing-cutover',
    category: 'billing',
    question: 'How do I schedule a price change for a future date?',
    tags: ['cutover', 'effective_from', 'change'],
    answer: (
      <p>
        Inside a rate card, "Cutover" lets you set an effective date for a
        new price. The system creates a new rate with
        <code className="mono"> effective_from = date</code> and trims the
        old one to end on that date. Wholesale floor validation still
        applies.
      </p>
    ),
  },
  {
    id: 'pricing-plan-override',
    category: 'billing',
    question: 'What is a plan override and when do I use one?',
    tags: ['override', 'plan', 'discount'],
    answer: (
      <p>
        An override forces a specific sell_price for a destination inside
        a plan, bypassing the rate card. Useful for one-off promos ("US at
        $0.005 until December") without spinning up a whole new rate card.
        The backend validates it's not below wholesale.
      </p>
    ),
  },
  {
    id: 'pricing-create-plan',
    category: 'billing',
    question: 'How do I create a plan my customers can choose?',
    tags: ['plan', 'subscription', 'create'],
    cta: { label: 'Go to Plans', to: '/admin/plans' },
    answer: (
      <p>
        Admin → <strong>Plans</strong> → "Create". Name, monthly fee,
        included SMS, overage. Point it at an existing rate card. When you
        assign a customer to the plan, that rate card becomes their default.
      </p>
    ),
  },

  // =============================================================================
  // Sender pool
  // =============================================================================
  {
    id: 'senders-pool',
    category: 'senders',
    question: 'How does the sender pool work?',
    tags: ['pool', 'admin senders'],
    cta: { label: 'Go to Senders', to: '/admin/senders' },
    answer: (
      <p>
        As a reseller you register senders in your pool, submit them for
        platform-admin 10DLC approval, and once approved assign them to
        specific customers. A given sender can only be assigned to 1
        customer at a time.
      </p>
    ),
  },
  {
    id: 'senders-submit',
    category: 'senders',
    question: 'How do I submit a sender for 10DLC approval?',
    tags: ['submit', 'approve'],
    answer: (
      <p>
        Admin → Senders → sender detail → "Submit for approval". It moves
        to <code className="mono">submitted</code> state and the platform
        admin reviews it. While pending you can't assign it. If approved
        it moves to <code className="mono">approved</code> and is usable.
      </p>
    ),
  },
  {
    id: 'senders-assign',
    category: 'senders',
    question: 'How do I assign an approved sender to a customer?',
    tags: ['assign'],
    answer: (
      <p>
        On the sender detail → "Assign" → pick the destination customer.
        The sender is "marked" for that customer and only that tenant can
        use it as the From of their sends. To reassign, "unassign" the
        current owner first.
      </p>
    ),
  },

  // =============================================================================
  // Earnings
  // =============================================================================
  {
    id: 'earnings-read',
    category: 'reports',
    question: 'How do I read the Earnings report?',
    tags: ['earnings', 'profit', 'margin'],
    cta: { label: 'Go to Earnings', to: '/admin/earnings' },
    answer: (
      <p>
        Earnings = sum(sell_price) - sum(buy_cost) over your customers' SMS
        in the range. The KPIs show revenue / cost / margin. The
        month-over-month chart and the top-customers list tell you where
        your margin comes from.
      </p>
    ),
  },

  // =============================================================================
  // Branding
  // =============================================================================
  {
    id: 'branding-setup',
    category: 'branding',
    question: 'How do I customize the portal with my brand (white-label)?',
    tags: ['white-label', 'logo', 'color'],
    cta: { label: 'Go to Branding', to: '/admin/branding' },
    answer: (
      <p>
        Admin → <strong>Branding</strong>. Logo (PNG/SVG), primary color
        (hex), custom domain (future), and transactional email branding.
        Changes apply to ALL your customers — the portal loads them at
        runtime when they log in.
      </p>
    ),
  },
  {
    id: 'branding-email-from',
    category: 'branding',
    question: "Can I change the 'from name' on emails my customers receive?",
    tags: ['email', 'from', 'sender'],
    answer: (
      <p>
        Yes — Branding → "From name" + "Reply-to". For example, instead of
        "EzSender", your client sees "Acme Notifications &lt;noreply@getezsender.com&gt;".
        The email DOMAIN stays the platform's (SPF/DKIM live there), but
        the display name is yours.
      </p>
    ),
  },
  {
    id: 'branding-custom-domain',
    category: 'branding',
    question: 'Can I use my own domain (portal.mycompany.com)?',
    tags: ['domain', 'dns', 'cname'],
    answer: (
      <p>
        On the roadmap but not yet shipped. For now all your customers use
        <code className="mono"> app.getezsender.com</code>. When the feature
        lands, you'll set a CNAME → the platform handles automatic TLS and
        your customers see your URL.
      </p>
    ),
  },

  // =============================================================================
  // Audit log
  // =============================================================================
  {
    id: 'audit-what',
    category: 'platform',
    question: 'What gets logged in the audit log?',
    tags: ['audit', 'log'],
    cta: { label: 'Go to Audit', to: '/admin/audit' },
    answer: (
      <p>
        Every state-changing call under <code className="mono">/v1/admin/*</code>:
        who, when, which endpoint, the request body (with sensitive fields
        redacted). Useful for "who created this customer?", "when was this
        rate changed?", etc. Filterable by actor + path + range.
      </p>
    ),
  },
];
